import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Mail, RotateCw } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { getApiErrorMessage } from '@/lib/api-client'
import { useResendVerification } from '../hooks'
import { AuthLayout } from './auth-layout'

export function VerificationPendingPage() {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const [resent, setResent] = useState(false)
  const mutation = useResendVerification()

  const handleResend = async () => {
    if (!email) {
      toast.error('No email provided', {
        description: 'Go back and enter your email to request a new link.',
      })
      return
    }
    try {
      await mutation.mutateAsync(email)
      setResent(true)
      toast.success('Verification email sent', { description: `Check ${email}'s inbox.` })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to resend verification email'))
    }
  }

  return (
    <AuthLayout
      title="Verify your email"
      description={
        email
          ? `We sent a verification link to ${email}.`
          : 'We sent a verification link to your email address.'
      }
      footer={
        <Link
          to={appRoutes.auth.login}
          className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to sign in
        </Link>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-border bg-white/[0.03] p-4">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Mail className="size-4" />
          </span>
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">One last step</p>
            <p className="text-muted-foreground">
              Open the email we just sent and click the verification link to activate your account.
            </p>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleResend}
          disabled={mutation.isPending}
        >
          <RotateCw className="size-4" />
          {mutation.isPending ? 'Sending...' : resent ? 'Resent — check your inbox' : 'Resend verification email'}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Already verified?{' '}
          <Link to={appRoutes.auth.login} className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}